import { useState } from 'react';

import { DESCENDING } from 'constants/sortDirections';
import { Table } from 'ui/table';
import { PaginationVariant } from 'ui/pagination';
import { paginate } from 'utils/arrayHelpers';

import { EmptyStateSection, EmptyStateType } from './EmptyStateSection';
import { CSV_EXPORT_HEADERS, TABLE_COLUMNS } from '../constants';
import { useSummaryQueryContext } from '../contexts/query';
import { useEventSelectionContext } from '../contexts/selection';
import { TableLoader } from './TableLoader';
import {
  HiddenCSVLink,
  StyledHeader,
  StyledPageSize,
  StyledPagination,
  StyledRows,
  TableFooter,
  TableWrapper,
  TotalEvents,
} from './styled';
import { ExtendedEventInfo } from '../types';
import { useEventFilterContext } from '../contexts/filter';

const PAGE_SIZES = [10, 20, 50, 100];

export const sortRows = (
  events: ExtendedEventInfo[],
  sortKey: keyof ExtendedEventInfo,
  sortDirection: string,
) =>
  events.sort((val1: Record<string, string | number>, val2: Record<string, string | number>) => {
    let formattedValue1 = val1[sortKey];
    let formattedValue2 = val2[sortKey];
    if (typeof formattedValue1 === 'string' && typeof formattedValue2 === 'string') {
      formattedValue1 = formattedValue1.toLowerCase();
      formattedValue2 = formattedValue2.toLowerCase();
    }
    if (sortDirection === DESCENDING) {
      return formattedValue1 > formattedValue2 ? -1 : 1;
    }
    return formattedValue2 > formattedValue1 ? -1 : 1;
  });

export const EventSummaryTable = () => {
  const { isError, isLoading } = useSummaryQueryContext();
  const { selected, types } = useEventSelectionContext();
  const { isFiltering } = useEventFilterContext();
  const [sortedBy, setSortedBy] = useState<keyof ExtendedEventInfo>('count');
  const [sortDirection, setSortDirection] = useState(DESCENDING);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const isFilteringData = isFiltering || selected.length < types.length;

  if (isLoading) {
    return <TableLoader />;
  }

  if (isError) {
    return <EmptyStateSection type={EmptyStateType.error} />;
  }

  const sortedEventRows = sortRows(selected, sortedBy, sortDirection);
  const totalUniqueEvents = sortedEventRows.length;

  if (totalUniqueEvents === 0) {
    return isFilteringData ? (
      <EmptyStateSection data-test="empty-state-filter" type={EmptyStateType.filter} />
    ) : (
      <EmptyStateSection data-test="empty-state-new-user" type={EmptyStateType.newUser} />
    );
  }

  const paginatedSortedRows = paginate(sortedEventRows, pageSize, currentPage);

  return (
    <TableWrapper>
      <HiddenCSVLink
        data={sortedEventRows}
        headers={CSV_EXPORT_HEADERS}
        filename="event_summary_table.csv"
        target="_blank"
      />
      <Table>
        <StyledHeader
          columns={TABLE_COLUMNS}
          sortedBy={sortedBy}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          isSticky
        />
        <StyledRows idKey="eventName" rows={paginatedSortedRows} columns={TABLE_COLUMNS} />
      </Table>
      {totalUniqueEvents > 0 && (
        <TableFooter>
          <TotalEvents>
            <span>{totalUniqueEvents} total</span>
          </TotalEvents>
          <StyledPagination
            pages={Math.ceil(totalUniqueEvents / pageSize)}
            onChangePage={setCurrentPage}
            page={currentPage}
            variant={PaginationVariant.Grey}
          />
          <StyledPageSize
            total={totalUniqueEvents}
            onSelect={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizes={PAGE_SIZES}
          />
        </TableFooter>
      )}
    </TableWrapper>
  );
};
